import { useState, useMemo } from 'react';

export interface Column<T> {
  key: string;
  label: string;
  sortable?: boolean;
  render?: (item: T) => React.ReactNode;
  className?: string;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  loading?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  searchFields?: (keyof T)[];
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  serverSearch?: boolean;
  serverSort?: boolean;
  sortValue?: { column: string | null; direction: 'asc' | 'desc' };
  onSortChange?: (column: string, direction: 'asc' | 'desc') => void;
  onRowClick?: (item: T) => void;
  emptyState?: React.ReactNode;
  actions?: (item: T) => React.ReactNode;
  rowKey: keyof T;
  className?: string;
  tableClassName?: string;
}

export default function DataTable<T extends Record<string, any>>({
  columns,
  data,
  loading = false,
  searchable = false,
  searchPlaceholder = 'Buscar...',
  searchFields = [],
  searchValue,
  onSearchChange,
  serverSearch = false,
  serverSort = false,
  sortValue,
  onSortChange,
  onRowClick,
  emptyState,
  actions,
  rowKey,
  className = '',
  tableClassName = 'table-fixed'
}: DataTableProps<T>) {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const activeSearchQuery = searchValue ?? searchQuery;
  const activeSortColumn = sortValue?.column ?? sortColumn;
  const activeSortDirection = sortValue?.direction ?? sortDirection;

  // Filter data based on search query
  const filteredData = useMemo(() => {
    if (serverSearch || !activeSearchQuery.trim()) return data;

    const query = activeSearchQuery.toLowerCase();
    const filtered = data.filter((item) => {
      // If searchFields are specified, search only those fields
      if (searchFields.length > 0) {
        return searchFields.some((field) => {
          const value = item[field];
          return value && String(value).toLowerCase().includes(query);
        });
      }

      // Otherwise, search all string/number fields
      return Object.values(item).some((value) => {
        if (value === null || value === undefined) return false;
        return String(value).toLowerCase().includes(query);
      });
    });

    // Prioritize exact ID matches
    return filtered.sort((a, b) => {
      const aId = String(a.id || '').toLowerCase();
      const bId = String(b.id || '').toLowerCase();
      const isAExactMatch = aId === query;
      const isBExactMatch = bId === query;

      if (isAExactMatch && !isBExactMatch) return -1;
      if (!isAExactMatch && isBExactMatch) return 1;
      return 0;
    });
  }, [data, activeSearchQuery, searchFields, serverSearch]);

  // Sort data
  const sortedData = useMemo(() => {
    if (serverSort || !activeSortColumn) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aValue = a[activeSortColumn];
      const bValue = b[activeSortColumn];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      const comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      return activeSortDirection === 'asc' ? comparison : -comparison;
    });
  }, [filteredData, activeSortColumn, activeSortDirection, serverSort]);

  const handleSort = (columnKey: string, sortable?: boolean) => {
    if (!sortable) return;

    if (serverSort) {
      const nextDirection =
        activeSortColumn === columnKey && activeSortDirection === 'asc' ? 'desc' : 'asc';
      onSortChange?.(columnKey, nextDirection);
      return;
    }

    if (sortColumn === columnKey) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
  };

  const handleRowClick = (item: T) => {
    if (onRowClick) {
      onRowClick(item);
    }
  };

  const handleSearchChange = (value: string) => {
    if (searchValue === undefined) {
      setSearchQuery(value);
    }
    onSearchChange?.(value);
  };

  // Default empty state
  const defaultEmptyState = (
    <div className="text-center py-12">
      <div className="text-6xl mb-4">📋</div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">No se encontraron resultados</h3>
      <p className="text-gray-600">
        {activeSearchQuery ? 'Intenta ajustar tu búsqueda' : 'No hay datos para mostrar'}
      </p>
    </div>
  );

  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 ${className}`}>
      {/* Search Bar */}
      {searchable && (
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder={searchPlaceholder}
              value={activeSearchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mb-2"></div>
            <p className="text-gray-600">Cargando datos...</p>
          </div>
        </div>
      ) : sortedData.length === 0 ? (
        // Empty State
        activeSearchQuery ? defaultEmptyState : (emptyState || defaultEmptyState)
      ) : (
        <>
          {/* Results Count */}
          <div className="px-6 py-3 border-b border-gray-200 bg-gray-50">
            <p className="text-sm text-gray-700">
              <span className="font-semibold">{sortedData.length}</span> resultado{sortedData.length !== 1 ? 's' : ''} encontrado{sortedData.length !== 1 ? 's' : ''}
              {activeSearchQuery && !serverSearch && <span className="text-gray-500 ml-1">(filtrado de {data.length} total{data.length !== 1 ? 'es' : ''})</span>}
            </p>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className={`w-full divide-y divide-gray-200 ${tableClassName}`}>
              <thead className="bg-gray-50">
                <tr>
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      className={`px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider break-words lg:px-4 2xl:px-6 ${
                        column.sortable ? 'cursor-pointer hover:bg-gray-100' : ''
                      } ${column.className || ''}`}
                      onClick={() => handleSort(column.key, column.sortable)}
                    >
                      <div className="flex items-center gap-1">
                        {column.label}
                        {column.sortable && (
                          <svg
                            className={`w-4 h-4 transition-transform ${
                              activeSortColumn === column.key
                                ? activeSortDirection === 'asc'
                                  ? 'rotate-180 text-gray-900'
                                  : 'text-gray-900'
                                : 'text-gray-400'
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        )}
                      </div>
                    </th>
                  ))}
                  {actions && (
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedData.map((item) => (
                  <tr
                    key={String(item[rowKey])}
                    className={`transition-colors ${
                      onRowClick ? 'cursor-pointer hover:bg-blue-50' : 'hover:bg-gray-50'
                    }`}
                    onClick={() => handleRowClick(item)}
                  >
                    {columns.map((column) => (
                      <td
                        key={column.key}
                        className={`px-3 py-3 text-sm text-gray-900 break-words lg:px-4 2xl:px-6 ${column.className || ''}`}
                      >
                        {column.render ? column.render(item) : String(item[column.key] ?? '')}
                      </td>
                    ))}
                    {actions && (
                      <td
                        className="px-6 py-4 whitespace-nowrap text-right text-sm"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {actions(item)}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
